import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    fetchReviewsByTourType,
    fetchReviewMediaByTourType,
    fetchReviewsByPackage,
    clearError,
    clearSuccess,
} from '../redux/slices/reviewSlice';
import CreateReview from '../Components/CreateReviw';

const ReviewList = ({ reviews, visibleReviews, openModal }) => {
    const [expandedReviewId, setExpandedReviewId] = useState(null);

    const toggleExpand = (id) => {
        setExpandedReviewId(expandedReviewId === id ? null : id);
    };

    return (
        <div className="space-y-4">
            {reviews.slice(0, visibleReviews).map((review) => {
                const isExpanded = expandedReviewId === review._id;
                const reviewMedia = Array.isArray(review.media) ? review.media : [];

                return (
                    <div key={review._id} className="border border-gray-300 p-4 rounded-lg bg-custom-white">
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center">
                                <img
                                    src="https://cdn.pixabay.com/photo/2023/02/18/11/00/icon-7797704_1280.png"
                                    alt="User"
                                    className="w-10 h-10 rounded-full mr-2"
                                />
                                <div>
                                    <div className="font-semibold text-custom-blue">{review.user}</div>
                                    <div className="text-sm text-gray-500">Reviewed: {review.date}</div>
                                </div>
                            </div>
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-custom-yellow fill-current" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                <span className="ml-1 text-custom-blue">{review.rating}/5</span>
                            </div>
                        </div>

                        <div className="text-sm text-gray-500 mb-2">Booked: {review.package}</div>

                        <div className="text-sm mb-2 text-custom-blue">
                            <p className={`${isExpanded ? '' : 'line-clamp-3'} overflow-hidden`}>
                                {review.comment}
                            </p>
                            {review.comment.length > 150 && (
                                <button
                                    onClick={() => toggleExpand(review._id)}
                                    className="text-custom-blue text-xs mt-1 hover:text-custom-blue/80"
                                >
                                    {isExpanded ? 'Show less' : 'Read more'}
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-4 gap-2">
                            {reviewMedia.slice(0, 3).map((item, index) => (
                                item.type === 'image' ? (
                                    <img
                                        key={index}
                                        src={item.url}
                                        alt={`Review media ${index + 1}`}
                                        className="w-full h-40 object-cover rounded-lg cursor-pointer"
                                        onClick={() => openModal(index, reviewMedia)}
                                    />
                                ) : (
                                    <video
                                        key={index}
                                        className="w-full h-40 object-cover rounded-lg cursor-pointer"
                                        onClick={() => openModal(index, reviewMedia)}
                                        controls
                                    >
                                        <source src={item.url} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                )
                            ))}
                            {reviewMedia.length > 3 && (
                                <div
                                    className="relative w-full h-40 rounded-lg overflow-hidden cursor-pointer"
                                    onClick={() => openModal(0, reviewMedia)}
                                >
                                    {reviewMedia[3].type === 'image' ? (
                                        <img
                                            src={reviewMedia[3].url}
                                            alt="View All"
                                            className="w-full h-full object-cover opacity-70"
                                        />
                                    ) : (
                                        <video
                                            className="w-full h-full object-cover opacity-70"
                                            muted
                                        >
                                            <source src={reviewMedia[3].url} type="video/mp4" />
                                        </video>
                                    )}
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-custom-white text-sm font-medium">
                                        View All ({reviewMedia.length}+)
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const ReviewComponent = ({ tourType, packageId }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        reviews,
        media,
        loading,
        error,
        total,
        pages,
        currentPage,
        success,
    } = useSelector((state) => state.reviews);
    const { isLoggedIn } = useSelector((state) => state.auth);

    const [visibleReviews, setVisibleReviews] = useState(4);
    const [tourStats, setTourStats] = useState({
        totalReviews: 0,
        ratings: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        averageRating: 0,
    });
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [modalMedia, setModalMedia] = useState([]);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);

    const reviewMedia = packageId
        ? reviews.reduce((acc, review) => [...acc, ...(Array.isArray(review.media) ? review.media : [])], [])
        : media;

    useEffect(() => {
        if (tourType && !packageId) {
            dispatch(fetchReviewsByTourType({ tourType, page: currentPage }));
            dispatch(fetchReviewMediaByTourType(tourType));
        } else if (packageId) {
            dispatch(fetchReviewsByPackage({ packageId, page: currentPage }));
        }
    }, [dispatch, tourType, packageId, currentPage]);

    useEffect(() => {
        if (loading) return;
        if (error) {
            console.error('Error fetching reviews:', error);
            dispatch(clearError());
            return;
        }

        const ratings = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        reviews.forEach((review) => {
            if (review.rating) {
                ratings[review.rating]++;
            }
        });
        const average = reviews.length
            ? (reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviews.length).toFixed(1)
            : 0;

        setTourStats({
            totalReviews: total,
            ratings,
            averageRating: average,
        });
    }, [reviews, total, loading, error, dispatch]);

    // Close review modal on successful submission
    useEffect(() => {
        if (success && isReviewModalOpen) {
            setIsReviewModalOpen(false);
            dispatch(clearSuccess());
        }
    }, [success, isReviewModalOpen, dispatch]);

    const loadMoreReviews = () => {
        if (visibleReviews < reviews.length) {
            setVisibleReviews((prev) => prev + 4);
        } else if (currentPage < pages) {
            dispatch(
                tourType && !packageId
                    ? fetchReviewsByTourType({ tourType, page: currentPage + 1 })
                    : fetchReviewsByPackage({ packageId, page: currentPage + 1 })
            );
        }
    };

    const openMediaModal = (index, mediaList) => {
        setModalMedia(mediaList);
        setCurrentMediaIndex(index);
        setIsMediaModalOpen(true);
    };

    const closeMediaModal = () => {
        setIsMediaModalOpen(false);
        setModalMedia([]);
        setCurrentMediaIndex(0);
    };

    const prevMedia = useCallback(() => {
        setCurrentMediaIndex((prev) => (prev === 0 ? modalMedia.length - 1 : prev - 1));
    }, [modalMedia.length]);

    const nextMedia = useCallback(() => {
        setCurrentMediaIndex((prev) => (prev === modalMedia.length - 1 ? 0 : prev + 1));
    }, [modalMedia.length]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isMediaModalOpen) return;
            if (e.key === 'ArrowLeft') {
                prevMedia();
            } else if (e.key === 'ArrowRight') {
                nextMedia();
            } else if (e.key === 'Escape') {
                closeMediaModal();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isMediaModalOpen, prevMedia, nextMedia]);

    const openReviewModal = () => {
        if (isLoggedIn) {
            setIsReviewModalOpen(true);
        } else {
            setShowLoginPrompt(true);
        }
    };

    const closeReviewModal = () => {
        setIsReviewModalOpen(false);
    };

    const handleLoginRedirect = () => {
        setShowLoginPrompt(false);
        navigate('/login', { state: { from: `/package/${packageId}` } });
    };

    return (
        <div className="p-4 max-w-4xl mx-auto bg-custom-white">
            {loading && <div className="text-custom-blue">Loading reviews...</div>}
            {error && <div className="text-red-500">Error: {error}</div>}

            <div className="w-full">
                <img
                    src="/new2.png"
                    alt="Traveler Testimonials"
                    className="w-full h-[300px] object-cover object-center"
                />
            </div>

            <div className="flex justify-end items-start mb-4">
                {packageId && (
                    <button
                        onClick={openReviewModal}
                        className={`px-4 py-2 bg-custom-blue text-custom-white rounded-lg hover:bg-custom-blue/90 disabled:bg-gray-400 disabled:cursor-not-allowed`}
                        disabled={!isLoggedIn}
                        title={!isLoggedIn ? 'Please log in to write a review' : ''}
                    >
                        Add Review
                    </button>
                )}
            </div>

            <div className="flex items-center">
                <div className="flex flex-col items-center mr-4">
                    <div className="flex">
                        {[...Array(5)].map((_, i) => (
                            <svg
                                key={i}
                                className="w-6 h-6 text-custom-yellow fill-current"
                                viewBox="0 0 20 20"
                            >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                        ))}
                    </div>
                    <span className="text-4xl font-bold text-custom-blue">{tourStats.averageRating}</span>
                </div>
                <div className="flex-1">
                    <div className="text-lg text-custom-blue mb-1">Reviews ({tourStats.totalReviews})</div>
                    {Object.keys(tourStats.ratings)
                        .reverse()
                        .map((star) => (
                            <div key={star} className="flex items-center mb-1">
                                <span className="w-8 text-custom-blue">{star} ★</span>
                                <div className="flex-1 h-2 bg-gray-200 rounded">
                                    <div
                                        className="h-2 bg-custom-yellow rounded"
                                        style={{
                                            width: `${tourStats.totalReviews
                                                ? (tourStats.ratings[star] / tourStats.totalReviews) * 100
                                                : 0
                                                }%`,
                                        }}
                                    ></div>
                                </div>
                                <span className="ml-2 text-sm text-custom-blue">{tourStats.ratings[star]}</span>
                            </div>
                        ))}
                </div>
            </div>

            {/* Login Prompt Modal */}
            {showLoginPrompt && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-custom-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold text-custom-blue mb-4">Login Required</h3>
                        <p className="text-gray-600 mb-6">Please log in to write a review.</p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={() => setShowLoginPrompt(false)}
                                className="px-4 py-2 text-custom-blue border border-gray-300 rounded-lg hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleLoginRedirect}
                                className="px-4 py-2 bg-custom-blue text-custom-white rounded-lg hover:bg-custom-blue/90"
                            >
                                Log In
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Review Modal */}
            {isReviewModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-custom-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold text-custom-blue">Write a Review</h2>
                            <button
                                onClick={closeReviewModal}
                                className="text-gray-600 hover:text-custom-blue"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                        <CreateReview packageId={packageId} tourType={tourType} />
                    </div>
                </div>
            )}

            <div className="mb-4">
                <div className="text-sm text-gray-500 mb-2">Traveller Media Gallery</div>
                {reviewMedia.length === 0 ? (
                    <div className="text-custom-blue">No media available</div>
                ) : (
                    <div className="flex gap-2">
                        <div className="w-1/2 relative overflow-hidden">
                            {reviewMedia[0].type === 'image' ? (
                                <img
                                    src={reviewMedia[0].url}
                                    alt="Gallery 1"
                                    className="w-full h-64 object-cover rounded-lg transition-transform duration-700 hover:scale-110 cursor-pointer"
                                    onClick={() => openMediaModal(0, reviewMedia)}
                                />
                            ) : (
                                <video
                                    className="w-full h-64 object-cover rounded-lg transition-transform duration-700 hover:scale-110 cursor-pointer"
                                    onClick={() => openMediaModal(0, reviewMedia)}
                                    controls
                                >
                                    <source src={reviewMedia[0].url} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                            <button
                                onClick={() => openMediaModal(0, reviewMedia)}
                                className="absolute bottom-6 left-2 flex items-center px-3 py-1 bg-custom-blue bg-opacity-50 text-custom-white text-sm rounded-full hover:bg-opacity-75"
                            >
                                <svg
                                    className="w-5 h-5 mr-1"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                                    ></path>
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M9 21v-6a2 2 0 012-2h2a2 2 0 012 2v6"
                                    ></path>
                                </svg>
                                View All ({reviewMedia.length})
                            </button>
                        </div>
                        <div className="w-1/2 grid grid-cols-2 gap-2">
                            {reviewMedia.slice(1, 5).map((item, index) => (
                                <div key={index} className="overflow-hidden">
                                    {item.type === 'image' ? (
                                        <img
                                            src={item.url}
                                            alt={`Gallery ${index + 2}`}
                                            className="w-full h-32 object-cover rounded-lg transition-transform duration-700 hover:scale-110 cursor-pointer"
                                            onClick={() => openMediaModal(index + 1, reviewMedia)}
                                        />
                                    ) : (
                                        <video
                                            className="w-full h-32 object-cover rounded-lg transition-transform duration-700 hover:scale-110 cursor-pointer"
                                            onClick={() => openMediaModal(index + 1, reviewMedia)}
                                            controls
                                        >
                                            <source src={item.url} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {isMediaModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex flex-col items-center justify-center z-50">
                    <div className="relative max-w-7xl w-full h-[100vh] bg-black flex flex-col">
                        <button
                            onClick={closeMediaModal}
                            className="absolute top-8 right-8 text-custom-white hover:text-gray-300 z-50"
                        >
                            <svg
                                className="w-8 h-8"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                ></path>
                            </svg>
                        </button>
                        <div className="relative flex-1 flex items-center justify-center">
                            {modalMedia[currentMediaIndex].type === 'image' ? (
                                <img
                                    src={modalMedia[currentMediaIndex].url}
                                    alt={`Gallery ${currentMediaIndex + 1}`}
                                    className="max-w-full max-h-[60vh] object-contain rounded-lg"
                                />
                            ) : (
                                <video
                                    className="max-w-full max-h-[60vh] object-contain rounded-lg"
                                    controls
                                >
                                    <source src={modalMedia[currentMediaIndex].url} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                            <button
                                onClick={prevMedia}
                                className="absolute left-8 top-1/2 transform -translate-y-1/2 text-custom-white hover:text-gray-300"
                            >
                                <svg
                                    className="w-8 h-8"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M15 19l-7-7 7-7"
                                    ></path>
                                </svg>
                            </button>
                            <button
                                onClick={nextMedia}
                                className="absolute right-8 top-1/2 transform -translate-y-1/2 text-custom-white hover:text-gray-300"
                            >
                                <svg
                                    className="w-8 h-8"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M9 5l7 7-7 7"
                                    ></path>
                                </svg>
                            </button>
                        </div>
                        <div className="text-center text-custom-white mt-2 mb-4">
                            {currentMediaIndex + 1} / {modalMedia.length}
                        </div>
                        <div className="flex justify-center items-center gap-2 overflow-x-auto pb-4">
                            {modalMedia.map((item, index) => (
                                item.type === 'image' ? (
                                    <img
                                        key={index}
                                        src={item.url}
                                        alt={`Thumbnail ${index + 1}`}
                                        className={`w-16 h-16 object-cover rounded-lg cursor-pointer ${currentMediaIndex === index ? 'border-2 border-custom-white' : ''}`}
                                        onClick={() => setCurrentMediaIndex(index)}
                                    />
                                ) : (
                                    <video
                                        key={index}
                                        className={`w-16 h-16 object-cover rounded-lg cursor-pointer ${currentMediaIndex === index ? 'border-2 border-custom-white' : ''}`}
                                        onClick={() => setCurrentMediaIndex(index)}
                                        muted
                                    >
                                        <source src={item.url} type="video/mp4" />
                                    </video>
                                )
                            ))}
                        </div>
                    </div>
                </div>
            )}
            <ReviewList reviews={reviews} visibleReviews={visibleReviews} openModal={openMediaModal} />
            {visibleReviews < total && (
                <button
                    onClick={loadMoreReviews}
                    className="mt-4 justify-center items-center px-4 py-2 bg-white text-custom-yellow border border-custom-yellow rounded-lg shadow-[0_0_8px_#facc15] hover:shadow-[0_0_12px_#facc15] hover:border-yellow-600"
                >
                    Load More Reviews ({total}+)
                </button>
            )}
        </div>
    );
};

export default ReviewComponent;